import {
  computed,
  nextTick,
  ref,
  watch,
  type ComputedRef,
  type CSSProperties,
  type Ref,
} from "vue";

/**
 * @description Minimal shape required for elements that support an "Other" option.
 */
export interface OtherOptionElement {
  id?: string;
  Id?: string;
  allowCustomOption?: boolean;
  otherOptionLabel?: string;
  otherOptionPlaceholder?: string;
  customfieldUpdated?: boolean;
}

/**
 * @description Minimal shape required for slides holding element arrays.
 */
export interface OtherOptionSlide<ElementType extends OtherOptionElement = OtherOptionElement> {
  slideData?: ElementType[];
}

/**
 * @description Minimal store shape needed to persist "Other option" related updates.
 * Covers both form (flat elements array) and survey/quiz (slides) layouts.
 */
export interface OtherOptionStore<ElementType extends OtherOptionElement = OtherOptionElement> {
  formId?: string | null;
  surveyId?: string | null;
  quizId?: string | null;
  anyUnsavedChanges: boolean;
  elements: ElementType[];
  slides: Array<OtherOptionSlide<ElementType>>;
  fieldStyle?: {
    width?: number | string | null;
  };
  /** Records the current state into the undo history. */
  addToHistory?: () => void;
}

type TranslateFn = (key: string) => string;

export const getDefaultOtherOptionPlaceholder = (t: TranslateFn) =>
  t("common.otherOptionPlaceholder");

export const getOtherOptionPlaceholderValue = <
  ElementType extends OtherOptionElement = OtherOptionElement
>(
  element: ElementType | null | undefined,
  t: TranslateFn
) => element?.otherOptionPlaceholder ?? getDefaultOtherOptionPlaceholder(t);

export const getOtherOptionPlaceholderHint = <
  ElementType extends OtherOptionElement = OtherOptionElement
>(
  element: ElementType | null | undefined,
  t: TranslateFn
) => (element?.otherOptionPlaceholder === "" ? "" : getDefaultOtherOptionPlaceholder(t));

const markCustomFieldUpdated = <ElementType extends OtherOptionElement>(
  element: ElementType
) => {
  if (element?.id || element?.Id) {
    element.customfieldUpdated = true;
  }
};

/**
 * @description Update the `otherOptionPlaceholder` for a given element inside the builder store.
 * Supports both survey (slideData) and forms (elements) and marks `anyUnsavedChanges`.
 *
 * @param store - The app store (subset) containing slides/elements.
 * @param element - The element reference to update.
 * @param value - The placeholder text to persist.
 * @returns True when an element in the store was updated; otherwise false.
 */
export const updateOtherOptionPlaceholderInStore = <ElementType extends OtherOptionElement>(
  store: OtherOptionStore<ElementType>,
  element: ElementType,
  value: string
): boolean => {
  if (!store || !element) {
    return false;
  }

  element.otherOptionPlaceholder = value;
  markCustomFieldUpdated(element);
  store.anyUnsavedChanges = true;
  return true;
};

/**
 * @description Persists the `allowCustomOption` flag for a given element inside the builder
 * store. Supports both form (flat `store.elements`) and survey/quiz (`store.slides`) layouts.
 * Marks unsaved changes and records a history entry on success.
 *
 * @param store - App store subset containing slides/elements and history helpers.
 * @param element - The element reference to update.
 * @param allowCustomOption - Whether the "Other" free-text option should be enabled.
 * @returns True when an element in the store was updated; otherwise false.
 */
export const updateAllowCustomOptionInStore = <ElementType extends OtherOptionElement>(
  store: OtherOptionStore<ElementType>,
  element: ElementType,
  allowCustomOption: boolean,
  defaultOtherOptionPlaceholder?: string
): boolean => {
  if (!store || !element) return false;

  element.allowCustomOption = allowCustomOption;
  if (
    allowCustomOption &&
    element.otherOptionPlaceholder == null &&
    defaultOtherOptionPlaceholder != null
  ) {
    element.otherOptionPlaceholder = defaultOtherOptionPlaceholder;
  }
  markCustomFieldUpdated(element);
  store.anyUnsavedChanges = true;
  store.addToHistory?.();
  return true;
};

interface UseOtherOptionPlaceholderEditorParams<ElementType extends OtherOptionElement> {
  element: Ref<ElementType> | ComputedRef<ElementType>;
  store: OtherOptionStore<ElementType>;
  t: TranslateFn;
  isActive?: Ref<boolean> | ComputedRef<boolean>;
}

/**
 * @description Shared logic for managing the "Other option" placeholder editing UX:
 * - provides translated defaults for label/placeholder
 * - keeps local input value in sync with store changes
 * - selects the full value on first focus for quicker editing
 *
 * @param params - Hook params.
 * @returns Reactive values + handlers for the "Other option" placeholder input.
 */
export const useOtherOptionPlaceholderEditor = <ElementType extends OtherOptionElement>(
  params: UseOtherOptionPlaceholderEditorParams<ElementType>
) => {
  const { element, store, t, isActive } = params;

  const shouldShowCustomOption = computed(() => !!element.value?.allowCustomOption);
  const defaultOtherPlaceholder = computed(() => getDefaultOtherOptionPlaceholder(t));
  const otherLabel = computed(() => t("common.otherOptionLabel"));
  const otherPlaceholder = computed(
    () => element.value?.otherOptionPlaceholder ?? defaultOtherPlaceholder.value
  );
  const otherPlaceholderHint = computed(() =>
    getOtherOptionPlaceholderHint(element.value, t)
  );
  const otherInputContainerStyle = computed((): CSSProperties => {
    const width = Number(store?.fieldStyle?.width);
    const hasWidth = Number.isFinite(width);
    return {
      width: isActive?.value ? "calc(100% - 1.75rem)" : "100%",
      maxWidth: hasWidth ? `${width}px` : "100%",
    } as CSSProperties;
  });

  const otherPlaceholderValue = ref(getOtherOptionPlaceholderValue(element.value, t));
  const committedOtherPlaceholderValue = ref(otherPlaceholderValue.value);
  const hasUncommittedPlaceholderChange = ref(false);
  const isFirstFocus = ref(true);

  watch(
    () =>
      [
        element.value?.allowCustomOption,
        element.value?.otherOptionPlaceholder,
        defaultOtherPlaceholder.value,
      ] as const,
    ([allowCustomOption, newVal, defaultPlaceholder], previousValues) => {
      const nextValue = newVal ?? defaultPlaceholder;
      if (allowCustomOption !== previousValues?.[0]) {
        isFirstFocus.value = true;
      }
      if (nextValue === otherPlaceholderValue.value) {
        return;
      }

      otherPlaceholderValue.value = nextValue;
      committedOtherPlaceholderValue.value = nextValue;
      hasUncommittedPlaceholderChange.value = false;
      isFirstFocus.value = true;
    },
    { immediate: true }
  );

  /**
   * @description Handles the first-focus UX for the "Other option" placeholder input.
   * Selects all existing text on the initial focus so the user can immediately retype
   * without manually clearing the default placeholder.
   *
   * @param {FocusEvent} event - The native focus event from the placeholder input element.
   * @returns {void}
   */
  const handleOtherPlaceholderFocus = (event: FocusEvent) => {
    const target = event.target as HTMLInputElement;
    if (!isFirstFocus.value || !target?.value) {
      return;
    }

    nextTick(() => {
      target.select();
      isFirstFocus.value = false;
    });
  };

  /**
   * @description Syncs the "Other option" placeholder text entered by the user back into
   * the builder store and the local reactive ref so the preview stays in sync.
   *
   * @param {Event | string} input - Native input event or UIInput model value.
   * @returns {void}
   */
  const updateOtherPlaceholder = (input: Event | string) => {
    const value =
      typeof input === "string"
        ? input
        : ((input.target as HTMLInputElement | null)?.value ?? "");
    otherPlaceholderValue.value = value;
    hasUncommittedPlaceholderChange.value =
      value !== committedOtherPlaceholderValue.value;

    updateOtherOptionPlaceholderInStore(store, element.value, value);
  };

  /**
   * @description Records one undo snapshot after a placeholder editing session,
   * but only when its committed value actually changed.
   *
   * @returns {void}
   */
  const commitOtherPlaceholder = () => {
    if (
      !hasUncommittedPlaceholderChange.value ||
      otherPlaceholderValue.value === committedOtherPlaceholderValue.value
    ) {
      return;
    }

    committedOtherPlaceholderValue.value = otherPlaceholderValue.value;
    hasUncommittedPlaceholderChange.value = false;
    store.addToHistory?.();
  };

  return {
    shouldShowCustomOption,
    otherLabel,
    otherPlaceholder,
    otherPlaceholderHint,
    otherInputContainerStyle,
    otherPlaceholderValue,
    handleOtherPlaceholderFocus,
    updateOtherPlaceholder,
    commitOtherPlaceholder,
  };
};
